// app/javascript/controllers/custom_scrollbar_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "customScrollbar", "thumb"]

  static values = {
    scrollbarID: String
  }

  ignoreScrollEvents = false
  
  connect() {
    document.addEventListener('DOMContentLoaded', () => {
      this.setupScrollbar()
      this.setupIntersectionObserver()
      this.setupSyncScrolling()
      this.handleResize = this.handleResize.bind(this)
      this.updateCustomScrollbarPosition = this.updateCustomScrollbarPosition.bind(this)
      
      window.addEventListener('resize', this.handleResize)
      window.addEventListener('changeTab', ()=>{ setTimeout(this.handleResize, 500) })
      window.addEventListener('scroll', this.updateCustomScrollbarPosition)

      // Hide the custom scrollbar but keep it in the DOM
      this.customScrollbarTarget.classList.add('hidden_scrollbar')
      this.updateCustomScrollbarPosition()
      this.checkIfScrollingNeeded()
      this.resizeObserver = new ResizeObserver(() => {
        this.checkIfScrollingNeeded()
      })
      this.resizeObserver.observe(this.containerTarget)
    })
  }

  disconnect() {
    if (this.scrollbarObserver) {
      this.scrollbarObserver.disconnect()
    }
    if (this.containerObserver) {
      this.containerObserver.disconnect()
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.updateCustomScrollbarPosition)
  }

  checkIfScrollingNeeded() {
    const containerWidth = this.containerTarget.clientWidth
    const contentWidth = this.containerTarget.scrollWidth
    
    if (contentWidth <= containerWidth) {
      this.customScrollbarTarget.classList.add('hidden_scrollbar')
      this.needsScrolling = false
    } else {
      this.needsScrolling = true
      this.updateCustomScrollbarVisibility()
    }
  }

  setupScrollbar() {
    this.updateThumbWidth()
    this.setupDraggableThumb()
  }

  setupIntersectionObserver() {
    this.scrollbarMarker = document.createElement('div')
    this.scrollbarMarker.classList.add('scrollbar-marker')
    this.scrollbarMarker.id = `${this.scrollbarID}-scrollbar-marker`
    this.containerTarget.parentNode.insertBefore(this.scrollbarMarker, this.containerTarget.nextSibling)
    this.scrollbarMarker.style.position = 'absolute'
    this.scrollbarMarker.style.bottom = '0'
    this.scrollbarMarker.style.left = '0'
    this.scrollbarMarker.style.width = '100%'
    this.scrollbarMarker.style.height = '1px'
    this.scrollbarMarker.style.visibility = 'hidden'
    this.scrollbarVisible = true
    this.containerVisible = true
    
    this.scrollbarObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.scrollbarVisible = entry.isIntersecting
        this.updateCustomScrollbarVisibility()
      })
    }, { threshold: 0 })
    
    this.scrollbarObserver.observe(this.scrollbarMarker)
    this.containerObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.containerVisible = entry.isIntersecting
        this.updateCustomScrollbarVisibility()
      })
    }, { threshold: 0.1 })
    
    this.containerObserver.observe(this.containerTarget)
  }
  
  updateCustomScrollbarVisibility() {
    if (!this.needsScrolling) {
      this.customScrollbarTarget.classList.add('hidden_scrollbar')
      return
    }
    if (!this.scrollbarVisible && this.containerVisible) {
      this.customScrollbarTarget.classList.remove('hidden_scrollbar')
      this.updateCustomScrollbarPosition()
    } else {
      this.customScrollbarTarget.classList.add('hidden_scrollbar')
    }
  }

  setupSyncScrolling() {
    this.virtualScroller = document.createElement('div')
    this.virtualScroller.classList.add('virtual-scroller')
    this.virtualScroller.id = `${this.scrollbarID}-virtual-scroller`
    this.virtualScroller.style.overflowX = 'auto'
    this.virtualScroller.style.width = '100%'
    this.virtualScroller.style.height = '1px'
    this.virtualScroller.style.visibility = 'hidden'
    this.virtualScroller.style.position = 'absolute'
    this.virtualScroller.style.bottom = '0'

    const spacer = document.createElement('div')
    spacer.style.width = `${this.containerTarget.scrollWidth}px`
    spacer.style.height = '1px'
    this.virtualScroller.appendChild(spacer)
    this.customScrollbarTarget.appendChild(this.virtualScroller)
    this.syncScroll(this.containerTarget, this.virtualScroller)
    this.syncScroll(this.virtualScroller, this.containerTarget)
    this.virtualScroller.addEventListener('scroll', () => {
      this.updateThumbPosition()
    })
  }

  updateCustomScrollbarPosition() {
    if (this.customScrollbarTarget.classList.contains('hidden')) return
    
    const containerRect = this.containerTarget.getBoundingClientRect()

    this.customScrollbarTarget.style.position = 'fixed'
    this.customScrollbarTarget.style.left = `${containerRect.left}px`
    this.customScrollbarTarget.style.width = `${containerRect.width}px`
    this.customScrollbarTarget.style.bottom = '0'
  }

  syncScroll(element1, element2) {
    element1.addEventListener('scroll', () => {
      if (this.ignoreScrollEvents) return
      
      this.ignoreScrollEvents = true
      element2.scrollLeft = element1.scrollLeft
      setTimeout(() => {
        this.ignoreScrollEvents = false
      }, 10)
    })
  }

  updateThumbWidth() {
    const containerWidth = this.containerTarget.scrollWidth
    const viewportWidth = this.containerTarget.clientWidth
    const thumbPercentage = (viewportWidth / containerWidth) * 100
    this.thumbTarget.style.width = `${thumbPercentage}%`
    if (this.virtualScroller && this.virtualScroller.firstChild) {
      this.virtualScroller.firstChild.style.width = `${containerWidth}px`
    }
  }

  updateThumbPosition() {
    const scrollLeft = this.virtualScroller.scrollLeft
    const maxScroll = this.virtualScroller.scrollWidth - this.virtualScroller.clientWidth
    const thumbPosition = (scrollLeft / maxScroll) * (this.customScrollbarTarget.clientWidth - this.thumbTarget.clientWidth)
    this.thumbTarget.style.transform = `translateX(${thumbPosition}px)`
  }

  setupDraggableThumb() {
    let isDragging = false
    let startPosition = 0
    let initialScrollLeft = 0
    
    this.thumbTarget.addEventListener('mousedown', (e) => {
      isDragging = true
      startPosition = e.clientX
      initialScrollLeft = this.virtualScroller.scrollLeft
      this.thumbTarget.classList.add('dragging')
      document.body.classList.add('select-none')

      e.preventDefault()
      e.stopPropagation()
    })
    
    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return
      
      const dx = e.clientX - startPosition
      const scrollbarWidth = this.customScrollbarTarget.clientWidth
      const thumbWidth = this.thumbTarget.clientWidth
      const scrollRatio = (this.virtualScroller.scrollWidth - this.virtualScroller.clientWidth) / (scrollbarWidth - thumbWidth)
      this.virtualScroller.scrollLeft = initialScrollLeft + dx * scrollRatio
    })
    
    document.addEventListener('mouseup', () => {
      isDragging = false
      this.thumbTarget.classList.remove('dragging')
      document.body.classList.remove('no-select')
    })
  }

  handleResize() {
    this.updateThumbWidth()
    this.updateThumbPosition()
    this.updateCustomScrollbarPosition()
    this.checkIfScrollingNeeded() 
  }
}